import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { Alert, Box, Chip, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import PageTopBar from "../../../components/common/PageTopBar";
import { fetchMySubstitutions } from "../services/substitutionService";
import { MySubstitution } from "../types";

/** My Space → My Substitutions: the classes this faculty covers for colleagues on leave, from today. */
export default function MySubstitutionsPage() {
  const [rows, setRows] = useState<MySubstitution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchMySubstitutions()
      .then(setRows)
      .catch(() => setError("Failed to load your substitutions."))
      .finally(() => setLoading(false));
  }, []);

  const byDate = useMemo(() => {
    const groups = new Map<string, MySubstitution[]>();
    rows.forEach((row) => groups.set(row.date, [...(groups.get(row.date) ?? []), row]));
    return Array.from(groups.entries());
  }, [rows]);

  const dateLabel = (date: string) => {
    const d = dayjs(date);
    if (d.isSame(dayjs(), "day")) return "Today";
    if (d.isSame(dayjs().add(1, "day"), "day")) return "Tomorrow";
    return d.format("dddd");
  };

  return (
    <Box sx={{ mt: 2, mr: 2 }}>
      <PageTopBar
        left={
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="h6" fontWeight={600}>
              My Substitutions
            </Typography>
            {rows.length > 0 && <Chip size="small" variant="outlined" label={`${rows.length} upcoming`} sx={{ fontWeight: 600 }} />}
          </Stack>
        }
      />

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress size={28} />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : rows.length === 0 ? (
        <Paper variant="outlined" sx={{ borderRadius: 2, py: 7, px: 2, textAlign: "center" }}>
          <SwapHorizRoundedIcon sx={{ fontSize: 40, color: "grey.400", mb: 1 }} />
          <Typography fontWeight={600}>No substitutions coming up</Typography>
          <Typography variant="body2" color="text.secondary">
            Classes you're asked to cover for a colleague on leave appear here.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2.5}>
          {byDate.map(([date, items]) => (
            <Box key={date}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                {dateLabel(date)}{" "}
                <Typography component="span" variant="body2" color="text.secondary">
                  · {dayjs(date).format("D MMM YYYY")}
                </Typography>
              </Typography>
              <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                {items.map((item, index) => (
                  <Stack
                    key={`${item.periodName}-${item.className}`}
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    sx={{ px: 2, py: 1.25, borderTop: index === 0 ? 0 : "1px solid", borderColor: "divider" }}
                  >
                    <Box sx={{ minWidth: 92 }}>
                      <Typography variant="body2" fontWeight={700}>
                        {item.periodName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.startTime}–{item.endTime}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {item.className} · {item.subjectName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Covering for {item.absentFacultyName}
                      </Typography>
                    </Box>
                  </Stack>
                ))}
              </Paper>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}
